/**
 * Where the mobile app lives. Every "get the app" CTA on the site routes
 * through these, so repointing a platform is a one-line change.
 *
 * `APP_INSTALL_PATH` is the short link the QR code and the mobile banner use:
 * it hits `/app`, which picks the store from the user agent. The committed QR
 * at `public/app-qr.svg` encodes the absolute form of that path, so regenerate
 * it if the domain ever changes:
 *
 *   pnpm dlx qrcode -t svg -o public/app-qr.svg "https://www.ezspot.ai/app"
 */

export const APP_STORE_URL =
  "https://apps.apple.com/us/app/ezspot/id6783127778";

// `com.ezspot.ai.app` is the production package in `~/lib/deep-links/app-association`.
export const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.ezspot.ai.app";

export const APP_INSTALL_PATH = "/app";

/** Every surface that links out to a store. */
export type BadgeSurface = "dashboard" | "landing" | "footer" | "mobile-banner";

/**
 * The single source of the analytics attribute. Clicks are measured through
 * PostHog autocapture, which preserves `data-attr`, so one `$autocapture`
 * insight broken down by it answers which surface drives installs with no
 * custom events to maintain. Going through this builder means a renamed surface
 * is a type error rather than a silently missing slice.
 *
 * Lives here rather than beside the badge component so the client-side mobile
 * banner can share it without pulling that component into the client bundle.
 */
export function badgeAttr(
  platform: "ios" | "android" | "universal",
  surface: BadgeSurface,
): string {
  return `app-badge-${platform}-${surface}`;
}
